import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';
import { EmoneySyncedFieldsCallout } from '@/modules/assetProviderIntegrations/shared/EmoneySyncedFieldsCallout';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { Debtor } from '@/modules/content/tooltipContent/Debtor';
import { Lender } from '@/modules/content/tooltipContent/Lender';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';

import { IntegrationLiabilitySelect } from './components/IntegrationLiabilitySelect';
import { useIssuerAndRecipientOptionsGrouped } from './hooks/useRecipientOptions';
import {
  LiabilityFormPaths,
  LiabilityFormShape,
} from './LiabilityModal.constants';
import { LiabilityPaymentDetailsForm } from './LiabilityPaymentDetailsForm';

const EMPTY_PREFILLED_FIELDS = new Set<keyof LiabilityFormShape>();

interface LiabilityFormProps {
  entityId?: string;
  /** When true, disables fields whose values are sourced from an integration */
  isIntegrationLinked?: boolean;
  /** When true, hides the IntegrationLiabilitySelect (e.g. in the side drawer) */
  hideIntegrationSelect?: boolean;
  /** Fields that were populated from the integration at prefill time (for the drawer case) */
  initialIntegrationPrefilledFields?: Set<keyof LiabilityFormShape>;
}

export function LiabilityForm({
  entityId,
  isIntegrationLinked: isIntegrationLinkedProp,
  hideIntegrationSelect,
  initialIntegrationPrefilledFields,
}: LiabilityFormProps) {
  const { control, setValue } = useFormContext<LiabilityFormShape>();
  const { disabled: isDisabled } = useFormFieldsDisabled();
  const { linkedHouseholdAssetProvider } = useHouseholdDetailsContext();
  const enableEmoneyAssetsLiabilities = useFeatureFlag(
    'enable_emoney_assets_liabilities'
  );

  // When rendering inside a modal/drawer, disable portals to prevent focus/accessibility issues
  const dropdownProps = useMemo(() => ({ disablePortal: true }), []);

  const groupedOptions = useIssuerAndRecipientOptionsGrouped({
    currentEntityId: entityId,
  });

  const { options, alternateSourceOptions } = groupedOptions;

  const showExpandedPaymentDetails = useWatch({
    control,
    name: '_showExpandedPaymentDetails',
  });

  const hasDifferentPaymentSource = useWatch({
    control,
    name: 'hasDifferentPaymentSource',
  });

  const integrationLiabilityId = useWatch({
    control,
    name: 'integrationLiabilityId',
  });
  // Use the prop if provided (e.g., from the linking step drawer), otherwise
  // derive from the form's integrationLiabilityId field
  const isIntegrationLinked =
    isIntegrationLinkedProp ?? !!integrationLiabilityId;

  // Track which fields were populated by the integration at prefill time.
  // This is stable state that doesn't change as the user edits the form,
  // preventing the self-disabling bug where typing into an empty field
  // would reactively disable it.
  const [integrationPrefilledFields, setIntegrationPrefilledFields] = useState(
    () => initialIntegrationPrefilledFields ?? EMPTY_PREFILLED_FIELDS
  );
  useEffect(() => {
    if (!initialIntegrationPrefilledFields) return;

    setIntegrationPrefilledFields(initialIntegrationPrefilledFields);
  }, [initialIntegrationPrefilledFields]);
  const isFieldPrefilledByIntegration = (field: keyof LiabilityFormShape) =>
    isIntegrationLinked && integrationPrefilledFields.has(field);

  // Only show integration select when the feature flag is enabled and the household is linked to eMoney
  const showIntegrationSelect =
    !hideIntegrationSelect &&
    enableEmoneyAssetsLiabilities &&
    linkedHouseholdAssetProvider === AssetIntegrationProviders.EMONEY;

  const handleDeletePaymentDetails = useCallback(() => {
    setValue('_showExpandedPaymentDetails', false);
  }, [setValue]);

  const handleAddPaymentDetails = useCallback(() => {
    setValue('_showExpandedPaymentDetails', true);
  }, [setValue]);

  return (
    <Stack spacing={2}>
      {showIntegrationSelect && (
        <IntegrationLiabilitySelect
          provider={linkedHouseholdAssetProvider!}
          onPrefill={setIntegrationPrefilledFields}
        />
      )}
      {isIntegrationLinked && <EmoneySyncedFieldsCallout />}
      <Box>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput<LiabilityFormShape>
              control={control}
              label="Display name"
              fieldName={'name' as const satisfies LiabilityFormPaths}
              required
              disabled={isFieldPrefilledByIntegration('name')}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem width={5}>
            <FormAwareTypeaheadSelectInput<LiabilityFormShape>
              required
              placeholder="Select a lender"
              control={control}
              label="Lender"
              contextualHelp={<Lender />}
              fieldName={'issuerId' as const satisfies LiabilityFormPaths}
              options={options}
              groupBy={(option) => option.groupName ?? ''}
              dropdownProps={dropdownProps}
            />
          </FormLayoutItem>
          <FormLayoutItem width={2}>
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="flex-end"
              height="100%"
              pb={1}
            >
              <ArrowRightIcon size={25} sx={{ color: COLORS.TEXT.SECONDARY }} />
            </Stack>
          </FormLayoutItem>
          <FormLayoutItem width={5}>
            <FormAwareTypeaheadSelectInput<LiabilityFormShape>
              required
              placeholder="Select a debtor"
              control={control}
              label="Debtor"
              contextualHelp={<Debtor />}
              fieldName={'recipientId' as const satisfies LiabilityFormPaths}
              options={options}
              groupBy={(option) => option.groupName ?? ''}
              dropdownProps={dropdownProps}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <Card
              variant={hasDifferentPaymentSource ? 'filled' : 'transparent'}
              sx={{ p: 2 }}
            >
              <Stack spacing={1}>
                <FormAwareSwitch
                  control={control}
                  labelPosition="right"
                  fieldName={
                    'hasDifferentPaymentSource' as const satisfies LiabilityFormPaths
                  }
                  contextualHelp={
                    <PopperContent body="Payment is sourced from the assets of the debtor entity by default unless a different payment source is specified." />
                  }
                  label="Specify different payment source instead of the debtor"
                />
                {hasDifferentPaymentSource && (
                  <Box pl={7}>
                    <FormAwareTypeaheadSelectInput<LiabilityFormShape>
                      placeholder="Choose a payment source"
                      control={control}
                      required
                      label="Alternate payment source"
                      hideLabel
                      fieldName={
                        'alternateSourceId' as const satisfies LiabilityFormPaths
                      }
                      options={alternateSourceOptions}
                      groupBy={(option) => option.groupName ?? ''}
                      dropdownProps={dropdownProps}
                    />
                  </Box>
                )}
              </Stack>
            </Card>
          </FormLayoutItem>
        </FormLayoutRow>
      </Box>
      <Card variant="filled" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row">
            <Typography variant="title-s" component="div">
              Balance details
            </Typography>
            <ContextualHelpTooltip>
              <PopperContent body="All liabilities are automatically paid off at first death in waterfall calculations." />
            </ContextualHelpTooltip>
          </Stack>
          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <FormAwareCurrencyInput<LiabilityFormShape>
                control={control}
                label="Outstanding balance"
                isDecimalJSInput
                fieldName={
                  'outstandingBalance' as const satisfies LiabilityFormPaths
                }
                required
                disabled={isFieldPrefilledByIntegration('outstandingBalance')}
              />
            </FormLayoutItem>
            <FormLayoutItem width={4}>
              <FormAwareDatePickerInput<LiabilityFormShape>
                control={control}
                label="As of date"
                fieldName={
                  'balanceAsOfDate' as const satisfies LiabilityFormPaths
                }
                required
                disabled={isFieldPrefilledByIntegration('balanceAsOfDate')}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </Stack>
      </Card>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <FormAwareTextInput<LiabilityFormShape>
            control={control}
            label="Notes"
            fieldName={'notes' as const satisfies LiabilityFormPaths}
            multiline
            rows={3}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <Box>
        {!showExpandedPaymentDetails && (
          <Button
            fullWidth
            variant="secondary"
            size="sm"
            startIcon={PlusIcon}
            onClick={handleAddPaymentDetails}
            disabled={isDisabled}
          >
            Add payment details
          </Button>
        )}
        {showExpandedPaymentDetails && (
          <Card variant="filled" sx={{ p: 2 }}>
            <LiabilityPaymentDetailsForm
              onDeletePaymentDetails={handleDeletePaymentDetails}
              integrationPrefilledFields={
                isIntegrationLinked
                  ? integrationPrefilledFields
                  : EMPTY_PREFILLED_FIELDS
              }
            />
          </Card>
        )}
      </Box>
    </Stack>
  );
}
